import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import { Toggle } from 'components/Toggle';
import { Button } from 'components/Button';
import { Modal } from 'components/Modal';
import { apiErrorHandler, limitText, NotificationTypes, SizeEnum } from 'utils';
import { useLazyQuery, useMutation } from '@apollo/client';
import { GET_USER_STORE_NOTIFICATIONS } from 'gql/queries';
import {
  CREATE_USER_STORE_NOTIFICATION,
  UPDATE_USER_STORE_NOTIFICATION,
  REMOVE_USER_STORE_NOTIFICATIONS,
  INSERT_STORE_USER_NOTIFICATIONS,
} from 'gql/mutations';
import { useUserId } from '@nhost/react';
import { withNotification } from 'components/Notification';
import { useStore } from 'context/store-context';
import { useUser } from 'context/user-context';
import { User } from 'types/user';
import { NotificationTemplates } from './Tracking/NotificationTemplates';
import { ReturnNotificationTemplates } from './Tracking/ReturnNotificationTemplates';
import { KlaviyoIntegration } from './KlaviyoIntegration';
import { OutboundShipmentAlerts } from './OutboundShipmentAlerts';
import SettingsViewWrapper from 'components/SettingsWrapperView';
import {
  BellIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  MailIcon,
  TruckIcon,
  UserGroupIcon,
  ReplyIcon,
  XCircleIcon,
  InboxIcon,
  FlagIcon,
  ClockIcon,
} from '@heroicons/react/outline';
import SharedResolutionSettingsNotice from './ResolutionPortal/SharedResolutionSettingsNotice';
import ReturnsSettingsTabs from './ResolutionPortal/ReturnsSettingsTabs';

type NotificationsTab = 'customer' | 'returns' | 'internal';

const merchantAlertConfig = [
  {
    key: 'claimNew',
    title: 'New issue filed',
    description: 'A customer files a new issue or claim.',
    icon: BellIcon,
  },
  {
    key: 'claimStatus',
    title: 'Issue status changed',
    description: 'An issue changes status or needs attention.',
    icon: CheckCircleIcon,
  },
  {
    key: 'claimReorder',
    title: 'Reorder issued',
    description: 'A replacement order is created for an issue.',
    icon: TruckIcon,
  },
  {
    key: 'claimRefund',
    title: 'Refund issued',
    description: 'A refund resolution is completed.',
    icon: CurrencyDollarIcon,
  },
];

const returnAlertConfig = [
  {
    key: 'returnFiled',
    title: 'Return or exchange filed',
    description: 'A customer files a return or exchange request.',
    icon: ReplyIcon,
  },
  {
    key: 'returnAwaitingApproval',
    title: 'Return or exchange awaiting approval',
    description: 'A return or exchange request needs approval.',
    icon: ClockIcon,
  },
  {
    key: 'returnDroppedOff',
    title: 'Shipment dropped off',
    description: 'A return shipment is dropped off.',
    icon: TruckIcon,
  },
  {
    key: 'returnInTransit',
    title: 'Shipment in transit',
    description: 'A return shipment is on its way back.',
    icon: TruckIcon,
  },
  {
    key: 'returnDelivered',
    title: 'Shipment delivered',
    description: 'A return shipment reaches your facility.',
    icon: TruckIcon,
  },
  {
    key: 'returnReceived',
    title: 'Returned item received',
    description: 'A returned item is marked received.',
    icon: InboxIcon,
  },
  {
    key: 'returnProcessed',
    title: 'Return or exchange processed',
    description:
      'A supported resolution is processed for a return or exchange request.',
    icon: CurrencyDollarIcon,
  },
  {
    key: 'returnExpired',
    title: 'Return or exchange expired',
    description: 'A return or exchange request expires.',
    icon: XCircleIcon,
  },
  {
    key: 'returnFlagged',
    title: 'Return or exchange flagged',
    description: 'A return or exchange request is flagged for review.',
    icon: FlagIcon,
  },
];

const sectionCardClass =
  'rounded-xl border border-[#d8dfeb] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.05)]';

const isMerchantAlertEnabled = (value?: boolean | null) => value !== false;

const emptyAlertPreferences = {
  claimNew: false,
  claimStatus: false,
  claimReorder: false,
  claimRefund: false,
  returnFiled: false,
  returnDroppedOff: false,
  returnInTransit: false,
  returnDelivered: false,
  returnReceived: false,
  returnProcessed: false,
  returnExpired: false,
  returnFlagged: false,
  returnAwaitingApproval: false,
};
type AlertPreferences = typeof emptyAlertPreferences;
const alertPreferenceKeys = Object.keys(
  emptyAlertPreferences,
) as (keyof AlertPreferences)[];
const readAlertPreferences = (row: any): AlertPreferences =>
  Object.fromEntries(
    alertPreferenceKeys.map((key) => [key, isMerchantAlertEnabled(row[key])]),
  ) as AlertPreferences;
const sameAlertPreferences = (
  left: AlertPreferences,
  right: AlertPreferences,
) => alertPreferenceKeys.every((key) => left[key] === right[key]);

const getInitials = (name?: string, email?: string) => {
  const source = name || email || 'User';
  const parts = source
    .replace(/@.*/, '')
    .split(/[\s.+_-]+/)
    .filter(Boolean);

  return (parts[0]?.[0] || 'U').toUpperCase();
};

const resolveNotificationsTab = (tab: string | null): NotificationsTab => {
  if (tab?.startsWith('return')) return 'returns';
  if (tab === 'internal') return 'internal';
  return 'customer';
};

const NotificationCardHeader = ({
  icon: Icon,
  title,
  description,
  badge,
}: {
  icon: React.FC<any>;
  title: string;
  description: string;
  badge?: string;
}) => (
  <div className="flex items-start justify-between gap-4">
    <div className="flex min-w-0 items-start gap-4">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#eef5ff] text-[#2563eb]">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <h2 className="text-lg font-semibold leading-snug text-[#0b1c30]">
          {title}
        </h2>
        <p className="mt-1 text-sm leading-6 text-[#64748b]">
          {limitText(description)}
        </p>
      </div>
    </div>
    {badge ? (
      <span className="inline-flex flex-shrink-0 items-center rounded-full bg-[#eef5ff] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#2563eb]">
        {badge}
      </span>
    ) : null}
  </div>
);

const SettingToggleRow = ({
  icon: Icon,
  title,
  description,
  enabled,
  onChange,
  disabled,
}: {
  icon: React.FC<any>;
  title: string;
  description: string;
  enabled: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) => (
  <div className="group flex items-center gap-3 rounded-lg border border-[#e5ebf4] bg-white px-3 py-2.5 transition-colors hover:border-[#b9cdf7] hover:bg-[#f8fbff]">
    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#eef5ff] text-[#2563eb]">
      <Icon className="h-4 w-4" aria-hidden="true" />
    </div>
    <div className="min-w-0 flex-1">
      <h4 className="text-sm font-semibold text-[#0b1c30]">{title}</h4>
      <p className="mt-0.5 text-xs leading-5 text-[#64748b]">
        {limitText(description)}
      </p>
    </div>
    <Toggle
      isEnabled={enabled}
      onChange={onChange}
      ariaLabel={title}
      isSmall
      disabled={disabled}
    />
  </div>
);

export const Notifications = withNotification(({ showNotification }: any) => {
  const { storeId, storeProperties } = useStore();
  const userId = useUserId();
  const user = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const notificationsTab = resolveNotificationsTab(searchParams.get('tab'));

  const [notifications, setNotifications] = useState(emptyAlertPreferences);
  const [unsubscribed, setUnsubscribed] = useState(false);
  const [isRecipientsModalOpen, setIsRecipientsModalOpen] = useState(false);
  const owner = useRef({ storeId, userId });
  if (owner.current.storeId !== storeId || owner.current.userId !== userId)
    owner.current = { storeId, userId };
  const scope = owner.current;
  const mounted = useRef(true);
  const activeWrite = useRef<{ scope: typeof scope; token: object } | null>(
    null,
  );
  const [writingScope, setWritingScope] = useState<typeof scope | null>(null);
  const [unverifiedScope, setUnverifiedScope] = useState<typeof scope | null>(
    null,
  );
  const blockedOwner = useRef<typeof scope | null>(null);
  const verified = useRef<{
    scope: typeof scope;
    values: AlertPreferences;
    exists: boolean;
    awaitingEcho: boolean;
  } | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const [updateUserStoreNotifications] = useMutation(
    UPDATE_USER_STORE_NOTIFICATION,
  );

  const [createUserStoreNotifications] = useMutation(
    CREATE_USER_STORE_NOTIFICATION,
  );

  const [removeUserStoreNotifications] = useMutation(
    REMOVE_USER_STORE_NOTIFICATIONS,
  );

  const [addUserStoreNotifications] = useMutation(
    INSERT_STORE_USER_NOTIFICATIONS,
  );

  const [
    getUserStoreNotifications,
    {
      loading,
      data,
      called: notificationQueryCalled,
      variables: notificationQueryVariables,
    },
  ] = useLazyQuery(GET_USER_STORE_NOTIFICATIONS, {
    onError(error) {
      const parsedError = apiErrorHandler(error);
      showNotification(NotificationTypes.error, parsedError?.message);
    },
  });

  useEffect(() => {
    if (storeId) {
      getUserStoreNotifications({ variables: { storeId } });
    }
  }, [storeId, getUserStoreNotifications]);

  const currentUserNotifications = useMemo(
    () =>
      data?.storeUserNotifications?.find(
        ({
          user: notificationUser,
          storeId: notificationStoreId,
        }: {
          user: User;
          storeId: string;
        }) =>
          notificationUser?.id === userId && notificationStoreId === storeId,
      ),
    [data, userId, storeId],
  );

  const queryReady =
    Boolean(storeId && userId) &&
    !loading &&
    notificationQueryCalled !== false &&
    Array.isArray(data?.storeUserNotifications) &&
    (!notificationQueryVariables?.storeId ||
      notificationQueryVariables.storeId === storeId) &&
    data.storeUserNotifications.every((row: any) => row.storeId === storeId);
  const alertSaving = writingScope === scope;
  const alertWriteLocked =
    alertSaving ||
    unverifiedScope === scope ||
    !queryReady ||
    verified.current?.scope !== scope;

  useEffect(() => {
    if (!queryReady) return;
    const values = currentUserNotifications
      ? readAlertPreferences(currentUserNotifications)
      : emptyAlertPreferences;
    // A delayed query must not replace the mutation's acknowledged values.
    if (
      verified.current?.scope === scope &&
      verified.current.awaitingEcho &&
      !sameAlertPreferences(verified.current.values, values)
    )
      return;
    verified.current = {
      scope,
      values,
      exists: Boolean(currentUserNotifications),
      awaitingEcho: false,
    };
    setNotifications({ ...values });
  }, [currentUserNotifications, queryReady, scope]);

  const people = useMemo(() => {
    if (storeProperties?.users?.length) return storeProperties.users;
    return (
      currentUserNotifications?.store?.users ||
      data?.storeUserNotifications?.[0]?.store?.users ||
      []
    );
  }, [currentUserNotifications, data, storeProperties?.users]);

  const outboundTeamMembers = useMemo(
    () =>
      people
        .map((person: any) => ({
          id: person?.user?.id || person?.id || '',
          displayName:
            person?.user?.displayName ||
            person?.displayName ||
            person?.user?.email ||
            person?.email ||
            'Store user',
          email: person?.user?.email || person?.email || '',
          role:
            person?.userRole?.role ||
            person?.role ||
            person?.defaultRole ||
            'Member',
        }))
        .filter((person: any) => person.id && person.email),
    [people],
  );

  const hasAnyMerchantAlertEnabled = useMemo(
    () => Object.values(notifications).some(Boolean),
    [notifications],
  );
  const isOnlyMerchantUser = people.length === 1;
  const isLoadingMerchantNotifications = loading && !data;
  const enabledMerchantAlertCount = merchantAlertConfig.filter(
    (item) => notifications[item.key as keyof typeof notifications],
  ).length;
  const enabledReturnAlertCount = returnAlertConfig.filter(
    (item) => notifications[item.key as keyof typeof notifications],
  ).length;
  const isReturnsSettings = notificationsTab === 'returns';

  const selectTab = (tab: NotificationsTab) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', tab);
    setSearchParams(nextParams, { replace: true });
  };

  const verificationMessage =
    'Alert settings could not be verified. Reload before making more changes.';
  const ownsWrite = (token: object) =>
    mounted.current &&
    owner.current === scope &&
    activeWrite.current?.token === token;
  const beginWrite = (recovery = false) => {
    if (
      !mounted.current ||
      owner.current !== scope ||
      !storeId ||
      !userId ||
      activeWrite.current?.scope === scope ||
      (!recovery &&
        (blockedOwner.current === scope ||
          !queryReady ||
          verified.current?.scope !== scope))
    )
      return null;
    const token = {};
    activeWrite.current = { scope, token };
    setWritingScope(scope);
    return token;
  };
  const checkedPreferences = (row: any): AlertPreferences => {
    if (
      row?.storeId !== storeId ||
      row?.userId !== userId ||
      !alertPreferenceKeys.every(
        (key) =>
          Object.prototype.hasOwnProperty.call(row, key) &&
          (row[key] === null || typeof row[key] === 'boolean'),
      )
    )
      throw new Error(verificationMessage);
    return readAlertPreferences(row);
  };
  const confirmPreferences = (
    token: object,
    values: AlertPreferences,
    exists: boolean,
  ) => {
    if (!ownsWrite(token)) return false;
    verified.current = { scope, values, exists, awaitingEcho: true };
    blockedOwner.current = null;
    setUnverifiedScope(null);
    setNotifications(values);
    return true;
  };
  const refreshConfirmedPreferences = async (token: object) => {
    const result = await getUserStoreNotifications({
      variables: { storeId },
      fetchPolicy: 'network-only',
    });
    if (!ownsWrite(token)) return false;
    const rows = result?.data?.storeUserNotifications;
    if (
      !Array.isArray(rows) ||
      rows.some((row: any) => row.storeId !== storeId)
    )
      throw new Error(verificationMessage);
    const row = rows.find((item: any) => item.userId === userId);
    return confirmPreferences(
      token,
      row ? checkedPreferences(row) : emptyAlertPreferences,
      Boolean(row),
    );
  };
  const blockUnverifiedWrite = (token: object) => {
    if (!ownsWrite(token)) return;
    blockedOwner.current = scope;
    setUnverifiedScope(scope);
  };
  const finishWrite = (token: object) => {
    if (!ownsWrite(token)) return;
    activeWrite.current = null;
    setWritingScope(null);
    setUnsubscribed(false);
  };
  const saveAlertPreferences = async (
    key?: keyof AlertPreferences,
    value?: boolean,
  ) => {
    const previous = verified.current;
    const bulk = key === undefined;
    if (bulk && (!previous?.exists || previous.scope !== scope)) return;
    const token = beginWrite();
    if (!token || !previous) return;
    setUnsubscribed(bulk);
    const next = bulk
      ? emptyAlertPreferences
      : { ...previous.values, [key!]: value! };
    const created = !previous.exists;
    try {
      const result = created
        ? await createUserStoreNotifications({
            variables: { storeId, userId, [key!]: value },
            refetchQueries: ['storeUserNotifications'],
          })
        : await updateUserStoreNotifications({
            variables: { userId, storeId, ...next },
          });
      if (!ownsWrite(token)) return;
      const values = checkedPreferences(
        result?.data?.[
          created
            ? 'insertStoreUserNotification'
            : 'updateStoreUserNotification'
        ],
      );
      if (
        created ? values[key!] !== value : !sameAlertPreferences(values, next)
      )
        throw new Error(verificationMessage);
      confirmPreferences(token, values, true);
      showNotification(
        NotificationTypes.success,
        bulk
          ? 'All alerts turned off'
          : result?.data?.message ||
              (created
                ? 'Internal alert settings created'
                : 'Internal alert settings updated'),
      );
    } catch (error: any) {
      if (!ownsWrite(token)) return;
      try {
        await refreshConfirmedPreferences(token);
      } catch {
        blockUnverifiedWrite(token);
      }
      if (!ownsWrite(token)) return;
      const parsedError = apiErrorHandler(error);
      showNotification(
        NotificationTypes.error,
        parsedError?.message || 'Internal alert setting could not be updated',
      );
    } finally {
      finishWrite(token);
    }
  };
  const handleSetNotification = (value: boolean, key: string) => {
    if (alertPreferenceKeys.includes(key as keyof AlertPreferences))
      return saveAlertPreferences(key as keyof AlertPreferences, value);
  };
  const handleUnsubscribeAll = () => saveAlertPreferences();
  const reloadAlertPreferences = async () => {
    const token = beginWrite(true);
    if (!token) return;
    try {
      await refreshConfirmedPreferences(token);
    } catch {
      blockUnverifiedWrite(token);
    } finally {
      finishWrite(token);
    }
  };

  const changeAlertRecipient = async (
    id: string | undefined,
    adding: boolean,
  ) => {
    if (!id || !storeId) return;
    const token = beginWrite();
    if (!token) return;
    try {
      if (adding) {
        await addUserStoreNotifications({
          variables: {
            object: {
              storeId,
              userId: id,
              claimNew: true,
              claimStatus: true,
              claimReorder: true,
              claimRefund: true,
              returnFiled: true,
              returnDroppedOff: true,
              returnInTransit: true,
              returnDelivered: true,
              returnReceived: true,
              returnProcessed: true,
              returnExpired: true,
              returnFlagged: true,
              returnAwaitingApproval: true,
            },
          },
          awaitRefetchQueries: true,
          refetchQueries: ['storeUserNotifications'],
        });
      } else {
        await removeUserStoreNotifications({
          variables: { userId: id, storeId },
          awaitRefetchQueries: true,
          refetchQueries: ['storeUserNotifications'],
        });
      }
      if (!ownsWrite(token)) return;
      if (id === userId) {
        await refreshConfirmedPreferences(token);
        if (!ownsWrite(token)) return;
        if (verified.current?.exists !== adding)
          throw new Error(verificationMessage);
      }
      showNotification(
        NotificationTypes.success,
        adding ? 'Recipient added' : 'Recipient removed',
      );
    } catch (error: any) {
      if (!ownsWrite(token)) return;
      if (id === userId) {
        try {
          await refreshConfirmedPreferences(token);
        } catch {
          blockUnverifiedWrite(token);
        }
      }
      if (!ownsWrite(token)) return;
      showNotification(
        NotificationTypes.error,
        apiErrorHandler(error)?.message || 'Recipient could not be updated',
      );
    } finally {
      finishWrite(token);
    }
  };
  const handleRemoveUser = (id?: string) => changeAlertRecipient(id, false);
  const handleAddUserNotifications = (id?: string) =>
    changeAlertRecipient(id, true);

  const recipientRows = people.map((person: any) => {
    const personRecord = person?.user ? person : { user: person };
    const personUserId = personRecord.user?.id;
    const displayName =
      personRecord.user?.displayName || personRecord.user?.email || 'User';
    const email = personRecord.user?.email || '';
    const isCurrentUser = user?.user?.email === email;
    const canManageRecipients = user?.role === 'ADMIN';
    const isEnabled = Boolean(
      personUserId &&
        data?.storeUserNotifications?.some(
          (notification: any) => notification.userId === personUserId,
        ),
    );

    return {
      person: personRecord,
      userId: personUserId,
      displayName,
      email,
      role:
        person?.userRole?.role ||
        person?.role ||
        person?.defaultRole ||
        'Member',
      isCurrentUser,
      canManageRecipients,
      isEnabled,
      isOnlyUserRow: canManageRecipients && isOnlyMerchantUser && isCurrentUser,
    };
  });

  return (
    <SettingsViewWrapper
      activeNav={
        isReturnsSettings
          ? 'resolution-portal/returns/notifications'
          : 'notifications'
      }
      sectionId={isReturnsSettings ? 'returns-settings' : undefined}
    >
      <div className="min-h-full bg-[#f6f6f7] text-[#0b1c30]">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 md:px-8">
          {isReturnsSettings ? (
            <div className="mb-8 space-y-6">
              <SharedResolutionSettingsNotice />
              <ReturnsSettingsTabs activeTab="notifications" />
            </div>
          ) : null}

          <header className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#2563eb]">
              Settings
            </p>
            <h1 className="mt-2 text-3xl font-bold leading-tight text-[#0b1c30]">
              Notifications
            </h1>
            <p className="mt-2 text-base leading-7 text-[#5c647a]">
              Manage customer emails and internal alerts.
            </p>
          </header>

          <nav
            role="tablist"
            aria-label="Notification settings"
            className="mb-6 flex max-w-xl rounded-lg border border-[#d8dfeb] bg-white p-1 shadow-sm"
          >
            {(
              [
                ['customer', 'Outbound'],
                ['returns', 'Returns & Exchanges'],
                ['internal', 'Internal'],
              ] as Array<[NotificationsTab, string]>
            ).map(([tab, label]) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={notificationsTab === tab}
                onClick={() => selectTab(tab)}
                className={classNames(
                  'flex-1 rounded-md px-4 py-2.5 text-sm font-semibold transition-colors',
                  notificationsTab === tab
                    ? 'bg-[#2563eb] text-white shadow-sm'
                    : 'text-[#5c647a] hover:bg-[#eff4ff] hover:text-[#0b1c30]',
                )}
              >
                {label}
              </button>
            ))}
          </nav>

          {notificationsTab === 'customer' ? (
            <div className="space-y-6">
              <KlaviyoIntegration />
              <section className={`${sectionCardClass} p-6`}>
                <NotificationCardHeader
                  icon={MailIcon}
                  title="Customer emails"
                  description="Choose the emails customers receive for delivery updates."
                />
                <div className="mt-6">
                  <NotificationTemplates embedded />
                </div>
              </section>
            </div>
          ) : null}

          {notificationsTab === 'returns' ? (
            <div className="space-y-6">
              <KlaviyoIntegration />
              <section className={`${sectionCardClass} p-6`}>
                <NotificationCardHeader
                  icon={ReplyIcon}
                  title="Return and exchange emails"
                  description="Choose customer emails for supported return and exchange updates."
                />
                <div className="mt-6">
                  <ReturnNotificationTemplates embedded />
                </div>
              </section>
            </div>
          ) : null}

          {notificationsTab === 'internal' ? (
            <div className="space-y-6">
              <OutboundShipmentAlerts
                storeId={storeId}
                teamMembers={outboundTeamMembers}
              />

              <section className={`${sectionCardClass} p-6`}>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <NotificationCardHeader
                    icon={BellIcon}
                    title="Issue, return and exchange alerts"
                    description="Choose the operational alerts your team receives."
                    badge={`${
                      enabledMerchantAlertCount + enabledReturnAlertCount
                    }/${
                      merchantAlertConfig.length + returnAlertConfig.length
                    } active`}
                  />
                  <button
                    type="button"
                    onClick={() => setIsRecipientsModalOpen(true)}
                    disabled={alertWriteLocked}
                    className="inline-flex flex-shrink-0 items-center justify-center gap-2 rounded-lg bg-[#0b1c30] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#2563eb]"
                  >
                    <UserGroupIcon className="h-4 w-4" aria-hidden="true" />
                    Manage alert recipients
                  </button>
                </div>

                {alertSaving && (
                  <p role="status" className="mt-4 text-sm text-gray-600">
                    Saving alert settings…
                  </p>
                )}
                {unverifiedScope === scope && (
                  <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p role="alert" className="text-sm text-amber-900">
                      {verificationMessage}
                    </p>
                    <Button
                      onClick={reloadAlertPreferences}
                      disabled={alertSaving}
                      variant="outlined"
                      className="mt-2"
                    >
                      Reload alert settings
                    </Button>
                  </div>
                )}

                {isLoadingMerchantNotifications ? (
                  <div className="flex min-h-[220px] items-center justify-center">
                    <div
                      className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-[#dbe7ff] border-t-[#2563eb]"
                      role="status"
                      aria-label="Loading internal alerts"
                    />
                  </div>
                ) : (
                  <div className="mt-6 grid gap-6 lg:grid-cols-2">
                    <div>
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div>
                          <h3 className="text-sm font-semibold text-[#0b1c30]">
                            Issue alerts
                          </h3>
                          <p className="mt-1 text-xs text-[#64748b]">
                            Claims and issue resolutions.
                          </p>
                        </div>
                        <span className="rounded-full bg-[#eef1f6] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
                          {enabledMerchantAlertCount}/
                          {merchantAlertConfig.length} active
                        </span>
                      </div>
                      <div className="space-y-2">
                        {merchantAlertConfig.map((item) => (
                          <SettingToggleRow
                            key={item.key}
                            icon={item.icon}
                            title={item.title}
                            description={item.description}
                            enabled={
                              notifications[
                                item.key as keyof typeof notifications
                              ]
                            }
                            onChange={(value) =>
                              handleSetNotification(value, item.key)
                            }
                            disabled={alertWriteLocked}
                          />
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div>
                          <h3 className="text-sm font-semibold text-[#0b1c30]">
                            Return &amp; exchange alerts
                          </h3>
                          <p className="mt-1 text-xs text-[#64748b]">
                            Supported return and exchange progress and review
                            events.
                          </p>
                        </div>
                        <span className="rounded-full bg-[#eef1f6] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
                          {enabledReturnAlertCount}/{returnAlertConfig.length}{' '}
                          active
                        </span>
                      </div>
                      <div className="space-y-2">
                        {returnAlertConfig.map((item) => (
                          <SettingToggleRow
                            key={item.key}
                            icon={item.icon}
                            title={item.title}
                            description={item.description}
                            enabled={
                              notifications[
                                item.key as keyof typeof notifications
                              ]
                            }
                            onChange={(value) =>
                              handleSetNotification(value, item.key)
                            }
                            disabled={alertWriteLocked}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {hasAnyMerchantAlertEnabled ? (
                  <Button
                    onClick={handleUnsubscribeAll}
                    size={SizeEnum.md}
                    color="danger"
                    variant="outlined"
                    className="mt-6 justify-center rounded-lg"
                    isLoading={unsubscribed && alertSaving}
                    disabled={alertWriteLocked}
                  >
                    Turn off my issue, return and exchange alerts
                  </Button>
                ) : null}
              </section>
            </div>
          ) : null}

          <Modal
            open={isRecipientsModalOpen}
            openModal={setIsRecipientsModalOpen}
            containerClass="w-full max-w-4xl"
          >
            <div className="mb-5">
              <h2 className="text-lg font-semibold text-[#0b1c30]">
                Issue, return and exchange recipients
              </h2>
              <p className="mt-1 text-sm text-[#64748b]">
                Choose store users for issue, return and exchange alerts.
                Outbound shipment recipients are managed separately above.
              </p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-[#d8dfeb]">
              <table className="min-w-full text-left">
                <thead>
                  <tr className="bg-[#eff4ff] text-xs font-bold uppercase tracking-wide text-[#737686]">
                    <th className="px-6 py-4">Name</th>
                    <th className="px-6 py-4">Email</th>
                    <th className="px-6 py-4">Role</th>
                    <th className="px-6 py-4 text-right">Receive alerts</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e5ebf4] bg-white">
                  {recipientRows.length ? (
                    recipientRows.map((row: any) => (
                      <tr key={row.person?.user?.id || row.email}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#dbe7ff] text-xs font-bold text-[#2563eb]">
                              {getInitials(row.displayName, row.email)}
                            </div>
                            <span className="max-w-[240px] truncate text-sm font-semibold text-[#0b1c30]">
                              {limitText(row.displayName)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#5c647a]">
                          {limitText(row.email)}
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex rounded border border-[#d8dfeb] bg-[#f8fbff] px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-[#5c647a]">
                            {row.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          {row.isOnlyUserRow ? (
                            <span className="text-xs text-[#737686]">
                              Only store user
                            </span>
                          ) : !row.isCurrentUser && row.canManageRecipients ? (
                            <Toggle
                              isEnabled={row.isEnabled}
                              onChange={
                                row.isEnabled
                                  ? () => handleRemoveUser(row.userId)
                                  : () => handleAddUserNotifications(row.userId)
                              }
                              ariaLabel={`${row.isEnabled ? 'Remove' : 'Add'} ${
                                row.email
                              }`}
                              isSmall
                              disabled={
                                !row.userId || !storeId || alertWriteLocked
                              }
                            />
                          ) : row.isCurrentUser ? (
                            <span className="text-xs italic text-[#737686]">
                              Current user
                            </span>
                          ) : (
                            <span className="text-xs italic text-[#737686]">
                              Managed by admin
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-6 py-10 text-center text-sm text-[#737686]"
                      >
                        No store users available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Modal>
        </div>
      </div>
    </SettingsViewWrapper>
  );
});
